import {Inject, Injectable} from '@angular/core';
import {Field} from './model/field.model';
import {Line} from './model/line.model';
import {Tree} from './model/tree.model';
import {Firestore, CollectionReference, DocumentReference, DocumentData} from '@angular/fire/firestore';
import {Observable} from 'rxjs';
import {TruffleFound} from './model/truffelFound.model';
import {User} from './model/user.model';
import {Sponsor} from './model/sponsor.model';
import {Auth} from '@angular/fire/auth';
import {Bonus} from './model/bonus.model';
import {shareReplay} from 'rxjs/operators';
import {AUTH_INTEROP, AuthInterop, FIRESTORE_INTEROP, FirestoreInterop} from './firebase-interop';

@Injectable({
  providedIn: 'root'
})
export class StateService {
  fields: Field[] = [];
  field: Field;
  line: Line;
  tree: Tree;
  truffleFound: TruffleFound;
  users: User[] = [];
  user: User;
  bonuses: Bonus[] = [];
  bonus: Bonus;

  private fieldsCollection: CollectionReference<DocumentData>;
  private fieldsObservable: Observable<Field[]>;
  private fieldDoc: DocumentReference<DocumentData>;
  private fieldObservable: Observable<Field | undefined>;
  private readonly fieldCache = new Map<string, {doc: DocumentReference<DocumentData>, observable: Observable<Field | undefined>}>();
  private usersCollection?: CollectionReference<DocumentData>;
  private usersObservable?: Observable<User[]>;
  private bonusesObservable: Observable<Bonus[]>;
  private bonusesCollection: CollectionReference<DocumentData>;

  constructor(
      private firestore: Firestore,
      private auth: Auth,
      @Inject(FIRESTORE_INTEROP) private firestoreInterop: FirestoreInterop,
      @Inject(AUTH_INTEROP) private authInterop: AuthInterop
  ) {
    this.fieldsCollection = this.firestoreInterop.collection(this.firestore, 'fields');
    this.fieldsObservable = this.firestoreInterop.collectionData<Field>(this.fieldsCollection as any, {idField: 'id'}).pipe(
      shareReplay({bufferSize: 1, refCount: true})
    );
    this.fieldsObservable.subscribe(fields => {
      this.fields = fields;
    });

    this.bonusesCollection = this.firestoreInterop.collection(this.firestore, 'bonuses');
    this.bonusesObservable = this.firestoreInterop.collectionData<Bonus>(this.bonusesCollection as any, {idField: 'id'}).pipe(
      shareReplay({bufferSize: 1, refCount: true})
    );
    this.bonusesObservable.subscribe(bonuses => {
      this.bonuses = bonuses;
    });
  }

  getFields(): Observable<Field[]> {
    return this.fieldsObservable;
  }

  addField(field: Field) {
    this.field = field;
    this.firestoreInterop.addDoc(this.fieldsCollection, this.toFirestore(field)).then(res => {
      this.field.id = res.id;
      this.firestoreInterop.setDoc(res, this.toFirestore(this.field));
    });
  }

  updateField(field: Field) {
    const docRef = this.firestoreInterop.doc(this.firestore, `fields/${field.id}`);
    this.firestoreInterop.updateDoc(docRef, this.toFirestore(field) as any);
  }

  getField(fieldId: string) {
    if (!fieldId) {
      return this.fieldObservable;
    }
    if (!this.fieldCache.has(fieldId)) {
      const docRef = this.firestoreInterop.doc(this.firestore, `fields/${fieldId}`);
      const observable = this.firestoreInterop.docData<Field>(docRef as DocumentReference<Field>, {idField: 'id'}).pipe(
        shareReplay({bufferSize: 1, refCount: true})
      );
      this.fieldCache.set(fieldId, {doc: docRef, observable});
    }
    const cached = this.fieldCache.get(fieldId)!;
    this.fieldDoc = cached.doc;
    this.fieldObservable = cached.observable;
    this.fieldObservable.subscribe(field => {
      this.field = field;
    });
    return this.fieldObservable;
  }

  deleteField(field: Field) {
    const index: number = this.fields.indexOf(field);
    if (index !== -1) {
      this.fields.splice(index, 1);
    }
    const docRef = this.firestoreInterop.doc(this.firestore, `fields/${field.id}`);
    this.firestoreInterop.deleteDoc(docRef).catch(err => console.log(err));
  }

  addLine() {
    if (!this.field) {
      return;
    }
    this.field.lines.push({trees: [], id: this.field.lines.length + 1});
    this.persistCurrentField();
  }

  deleteLine(field: Field) {
    const docRef = this.firestoreInterop.doc(this.firestore, `fields/${field.id}`);
    this.firestoreInterop.updateDoc(docRef, this.toFirestore(field) as any);
  }

  addTree(field: Field) {
    const docRef = this.firestoreInterop.doc(this.firestore, `fields/${field.id}`);
    this.firestoreInterop.updateDoc(docRef, this.toFirestore(field) as any);
  }

  updateTree(tree: Tree) {
    if (!this.field || !this.line || !this.tree) {
      return;
    }
    this.ensureUsersStream();
    // update tree
    this.field.lines[this.line.id - 1].trees[this.tree.id - 1] = this.tree;
    if (tree.sponsor) {
      this.field.lines[this.line.id - 1].trees[tree.id - 1].sponsor = JSON.parse(JSON.stringify(tree.sponsor));
      this.field.lines[this.line.id - 1].trees[tree.id - 1].sponsorSince = tree.sponsorSince;
    } else {
      this.field.lines[this.line.id - 1].trees[tree.id - 1].sponsor = undefined;
      this.field.lines[this.line.id - 1].trees[tree.id - 1].sponsorSince = undefined;
      tree.sponsorSince = undefined;
    }
    this.persistCurrentField();
    // update user
    const ref = this.field.id + ':' + this.line.id + ':' + tree.id;
    this.users.forEach(user => {
      if (tree.sponsor && user.id === tree.sponsor.id) {
        if (!user.treeReferences) {
          user.treeReferences = [];
        }
        if (!user.treeReferenceDates) {
          user.treeReferenceDates = {};
        }
        const storedDate = user.treeReferenceDates[ref];
        const sponsorSince = tree.sponsorSince ?? storedDate ?? new Date().toISOString();
        tree.sponsorSince = sponsorSince;
        user.treeReferenceDates[ref] = sponsorSince;
        if (user.treeReferences.indexOf(ref) === -1) {
          user.treeReferences.push(ref);
        }
        this.firestoreInterop.updateDoc(this.firestoreInterop.doc(this.firestore, `users/${user.id}`), this.toFirestore(user) as any);
      } else {
        let indexOf = -1;
        if (user.treeReferences) {
          indexOf = user.treeReferences.indexOf(ref);
        }
        if (indexOf !== -1) {
          user.treeReferences.splice(indexOf, 1);
          if (user.treeReferenceDates && user.treeReferenceDates[ref]) {
            delete user.treeReferenceDates[ref];
          }
          this.firestoreInterop.updateDoc(this.firestoreInterop.doc(this.firestore, `users/${user.id}`), this.toFirestore(user) as any);
        }
      }
    });
  }

  deleteTree(field: Field) {
    const docRef = this.firestoreInterop.doc(this.firestore, `fields/${field.id}`);
    this.firestoreInterop.updateDoc(docRef, this.toFirestore(field) as any);
  }

  addTruffle(field: Field) {
    const docRef = this.firestoreInterop.doc(this.firestore, `fields/${field.id}`);
    this.firestoreInterop.updateDoc(docRef, this.toFirestore(field) as any);
  }

  editTruffle(field: Field) {
    const docRef = this.firestoreInterop.doc(this.firestore, `fields/${field.id}`);
    this.firestoreInterop.updateDoc(docRef, this.toFirestore(field) as any);
  }

  deleteTruffle(field: Field) {
    const docRef = this.firestoreInterop.doc(this.firestore, `fields/${field.id}`);
    this.firestoreInterop.updateDoc(docRef, this.toFirestore(field) as any);
  }

  addUser(user: User, password: string) {
    this.user = user;
    this.authInterop.createUserWithEmailAndPassword(this.auth, user.email, password).then(res => {
      this.user.id = res.user.uid;
      const docRef = this.firestoreInterop.doc(this.firestore, `users/${res.user.uid}`);
      this.firestoreInterop.setDoc(docRef, this.toFirestore(this.user)).then(result => {
        console.log('User is added:' + result);
      });
    });

  }

  getUsers() {
    this.ensureUsersStream();
    return this.usersObservable!;
  }

  editUser(user: User) {
    const docRef = this.firestoreInterop.doc(this.firestore, `users/${user.id}`);
    this.firestoreInterop.setDoc(docRef, this.toFirestore(user)).then(() => {
      // this.authenticationService.updateUser(res);
    });
  }

  deleteUser(user: User) {
    this.ensureUsersStream();
    const index: number = this.users.indexOf(user);
    if (index !== -1) {
      this.users.splice(index, 1);
    }
    const docRef = this.firestoreInterop.doc(this.firestore, `users/${user.id}`);
    this.firestoreInterop.deleteDoc(docRef).catch(err => {
      // this.authenticationService.deleteUser(user.id);
      // this.angularFireAuth.auth.app.auth()..auth.rem.deleteUser(uid);
      // this.angularFireAuth.authState.subscribe(u => { // here gets current user :/, not selected
      //   console.log(u);
      //   u.delete().then(res => {
        //     console.log(res);
        //   });
      // });
      console.log(err);
    });
  }

  getSponsors(): Sponsor[] {
    this.ensureUsersStream();
    const sponsors: Sponsor[] = [];
    this.users.forEach(user => {
      sponsors.push(new Sponsor(user.id, user.salutation, user.firstName, user.lastname));
    });
    return sponsors;
  }

  getBonuses() {
    return this.bonusesObservable;
  }

  deleteBonus(bonus: Bonus) {
    const index: number = this.bonuses.indexOf(bonus);
    if (index !== -1) {
      this.bonuses.splice(index, 1);
    }
    const docRef = this.firestoreInterop.doc(this.firestore, `bonuses/${bonus.id}`);
    this.firestoreInterop.deleteDoc(docRef).catch(err => console.log(err));
  }

  editBonus(bonus: Bonus) {
    const docRef = this.firestoreInterop.doc(this.firestore, `bonuses/${bonus.id}`);
    this.firestoreInterop.updateDoc(docRef, this.toFirestore(bonus) as any);
  }

  addBonus(bonus: Bonus) {
    this.bonus = bonus;
    this.firestoreInterop.addDoc(this.bonusesCollection, this.toFirestore(bonus)).then(res => {
      this.bonus.id = res.id;
      this.firestoreInterop.setDoc(res, this.toFirestore(this.bonus));
    });
  }

  getUser(userId: string) {
    const docRef = this.firestoreInterop.doc(this.firestore, `users/${userId}`) as DocumentReference<User>;
    return this.firestoreInterop.docData<User>(docRef, {idField: 'id'});
  }

  private persistCurrentField() {
    if (!this.field || !this.fieldDoc) {
      return;
    }
    this.firestoreInterop.updateDoc(this.fieldDoc, this.toFirestore(this.field) as any);
  }

  private ensureUsersStream() {
    if (this.usersObservable) {
      return;
    }
    this.usersCollection = this.firestoreInterop.collection(this.firestore, 'users');
    this.usersObservable = this.firestoreInterop.collectionData<User>(this.usersCollection as any, {idField: 'id'}).pipe(
      shareReplay({bufferSize: 1, refCount: true})
    );
    this.usersObservable.subscribe(users => {
      this.users = users;
    });
  }

  private toFirestore<T>(value: T): Record<string, unknown> {
    return JSON.parse(JSON.stringify(value));
  }
}
